import { useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import NewNavigation from './NewNavigation';
import PreAuctionPremiumBanner from './PreAuctionPremiumBanner';
import PaymentInfoBanner from './PaymentInfoBanner';
import { useScrollThreshold } from '@/hooks/useScrollThreshold';

const HEIGHT_EPSILON = 0.5;

const NavigationWrapper: React.FC = () => {
  const navRef = useRef<HTMLDivElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const { scrolled } = useScrollThreshold();

  const handleOpenDashboard = useCallback((tab: string) => {
    // Use the window global set by NewNavigation
    if ((window as any).__openDashboard) {
      (window as any).__openDashboard(tab);
    }
  }, []);

  const applySizes = useMemo(() => {
    if (typeof document === 'undefined') {
      return () => {};
    }

    const root = document.documentElement;
    const lastHeightRef = { current: 0 };

    return () => {
      if (!navRef.current) return;
      const height = navRef.current.offsetHeight;
      if (Math.abs(height - lastHeightRef.current) <= HEIGHT_EPSILON) return;
      lastHeightRef.current = height;
      const pxHeight = `${height}px`;
      root.style.setProperty('--fh-nav-height', pxHeight);
      root.style.setProperty('--fh-nav-height-sm', pxHeight);
    };
  }, []);

  useLayoutEffect(() => {
    const cleanup = cleanupRef.current;
    cleanup?.();
    cleanupRef.current = null;

    applySizes();

    if (typeof ResizeObserver === 'undefined') {
      const handleResize = () => applySizes();
      window.addEventListener('resize', handleResize, { passive: true });
      cleanupRef.current = () => window.removeEventListener('resize', handleResize);
      return () => {
        cleanupRef.current?.();
      };
    }

    const resizeObserver = new ResizeObserver(() => applySizes());
    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    cleanupRef.current = () => resizeObserver.disconnect();

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [applySizes]);

  return (
    <div className="relative" role="banner" style={{ marginTop: 'var(--maintenance-banner-height, 0px)' }}>
      {/* Pre-Auction Premium Banner - appears at the very top */}
      <PreAuctionPremiumBanner />
      
      {/* Payment Info Banner - not sticky, appears above nav */}
      <PaymentInfoBanner onOpenDashboard={handleOpenDashboard} />
      
      {/* Sticky Navigation - Adjusted for maintenance banner */}
      <div 
        ref={navRef} 
        className="sticky z-[60]" 
        style={{ top: 'var(--maintenance-banner-height, 0px)' }}
      >
        <NewNavigation scrolled={scrolled} onOpenDashboard={handleOpenDashboard} />
      </div>
    </div>
  );
};

export default NavigationWrapper;